import Link from 'next/link'
import clsx from 'clsx'
import { Form, Formik } from 'formik'
import { toFormikValidationSchema } from 'zod-formik-adapter'

import { Button } from 'components/buttons'
import Input, { InputStyle } from 'components/Fields/Input'
import FieldSwitch from 'components/Fields/InputCheckbox'
import IconMenu from 'components/MenuIcon'
import Spinner from 'components/Spinner'

import signUpValidator from 'validators/signUpValidator'

import { IFormSignUpContentProps } from '../../types'

import useFormSignUp from './useFormSignUpContent'

const FormSignUpContent = ({
  type = 'Candidate',
  onSwitch,
  onSubmit,
  changeTab,
  isLoading,
  error,
  accType,
  hasAction,
  className
}: IFormSignUpContentProps) => {
  const { tabs, router, resetForm, descriptionTranslate, t, setResetForm } =
    useFormSignUp({ accType, hasAction })
  return (
    <div
      className={clsx(
        'relative z-0 flex h-screen w-full flex-col items-center justify-center overflow-auto bg-white sm:h-fit sm:w-[480px] md:rounded-lg md:shadow-lg',
        className
      )}
    >
      <div className="max-h-[100vh] overflow-auto py-5 sm:max-h-[90vh] sm:py-0">
        <div className="absolute top-14 right-5 sm:top-5 sm:right-5">
          <IconMenu
            isOpen
            onClick={() => {
              router.push('/')
            }}
          />
        </div>
        <h1 className="pt-10 text-center text-3xl font-semibold">
          {t('auth:signup.title')}
          <i className="dot"></i>
        </h1>
        <div className="">
          <div className="w-full text-center">{t('auth:signup.subtitle')}</div>
          <div className="mt-2 flex w-full justify-center font-semibold uppercase sm:mt-6">
            {tabs.map((item) => (
              <div
                key={item.value}
                className={clsx(
                  'cursor-pointer px-4 py-2 tracking-wide underline underline-offset-4 transition-all hover:text-red-550 focus:text-red-600',
                  accType === item.value && 'text-red-550'
                )}
                onClick={() => {
                  changeTab?.(item.value)
                  setResetForm((state) => state + 1)
                }}
              >
                {item.label}
              </div>
            ))}
          </div>
        </div>
        <div className="px-4 text-center text-sm text-gray-500">
          {t(`signup.description.${descriptionTranslate}.textPartFirst`, {
            ns: 'auth'
          })}
          <span className="text-red-500">
            {t(`signup.description.${descriptionTranslate}.redText`, {
              ns: 'auth'
            })}
          </span>
          {t(`signup.description.${descriptionTranslate}.textPartSecond`, {
            ns: 'auth'
          })}
        </div>
        <Formik
          key={resetForm}
          initialValues={{
            email: '',
            password: '',
            passwordConfirm: '',
            isAccept: false,
            firstName: '',
            lastName: ''
          }}
          validationSchema={toFormikValidationSchema(signUpValidator)}
          validateOnMount={true}
          onSubmit={onSubmit}
        >
          {({ errors }) => (
            <Form
              key={type}
              className="fade-in  -pt-4 mx-5 px-5 pb-5 sm:mt-5"
              noValidate
            >
              <Input
                name="firstName"
                label={t('inputs:firstName.label')}
                placeholder={t('inputs:firstName.placeholder')}
                required
              />
              <Input
                name="lastName"
                label={t('inputs:lastName.label')}
                placeholder={t('inputs:lastName.placeholder')}
                required
              />
              <Input
                name="email"
                label={t('inputs:email.label')}
                placeholder={t('inputs:email.placeholder')}
                type="email"
                required
              />
              <Input
                name="password"
                label={t('inputs:password.label')}
                placeholder={t('inputs:password.placeholder')}
                type="password"
                required
              />
              <Input
                name="passwordConfirm"
                label={t('inputs:passwordConfirm.label')}
                placeholder={t('inputs:passwordConfirm.placeholder')}
                type="password"
                required
              />
              <FieldSwitch
                name="isAccept"
                className="mt-5 mb-2 flex sm:mb-5 sm:pb-4"
                showError={
                  Object.keys(errors).length === 1 && !!errors.isAccept
                }
              >
                <span>
                  {t('auth:signup.terms.text')}{' '}
                  <Link
                    href="/terms-and-conditions"
                    className="text-medium underline transition-colors hover:text-red-550"
                    target="_blank"
                    referrerPolicy="no-referrer"
                  >
                    {t('auth:signup.terms.link')}
                  </Link>
                </span>
              </FieldSwitch>
              <div className="mb-1 w-full text-center text-gray-400">
                {t('auth:signup.redirect.text')}{' '}
                <Link
                  href={onSwitch ? '#' : '/sign-in'}
                  onClick={() => onSwitch?.('SignIn')}
                  className="text-medium underline transition-colors hover:text-red-550"
                >
                  {t('auth:signup.redirect.link')}
                </Link>
              </div>
              <Button
                disabled={isLoading || !!Object.keys(errors).length}
                type="submit"
                className="relative mx-auto flex items-center px-12"
              >
                <div>
                  <span className="absolute left-2 top-0 flex h-full items-center justify-center">
                    {isLoading && <Spinner size={32} isVisible />}
                  </span>
                  <span>{t('auth:signup.button')}</span>
                  {!!error && typeof error === 'string' && (
                    <div
                      className={clsx(
                        InputStyle.error,
                        'fade-in absolute left-0 bottom-[-1.25rem] w-full'
                      )}
                    >
                      {error}
                    </div>
                  )}
                </div>
              </Button>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  )
}

export default FormSignUpContent
